import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from 'shared/api';
import {
  getLeadCancelReasonLabel,
  LEAD_CANCEL_REASONS,
  LeadCancelReason,
  LEAD_CANCEL_REASON_LABELS,
} from 'shared/model/constants/lead-cancel-reason';
import { getLeadFormSourceLabel, getLeadPathLabel, getLeadTemplateLabel } from 'shared/model/constants/lead-source';
import {
  EDITABLE_LEAD_STATUSES,
  getLeadStatusClassName,
  getLeadStatusLabel,
  LeadStatus,
  normalizeLeadStatus,
} from 'shared/model/constants/lead-status';
import { ROUTE_PATHS } from 'shared/model/constants/paths';
import { ButtonCustom } from 'shared/ui/button-custom';
import { Loader } from 'shared/ui/loader';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './leads-view.module.scss';

type LeadType = 'selection' | 'insurance' | 'leasing';

type ManagerEditedEntry = {
  date: string;
  original_value: string | null;
};

type ManagerEditedFields = Record<string, ManagerEditedEntry>;

type EditableLeadField =
  | 'name'
  | 'phone'
  | 'comment'
  | 'type'
  | 'equipment'
  | 'condition'
  | 'objectType'
  | 'inn'
  | 'region'
  | 'amount'
  | 'advance'
  | 'deadline';

interface Lead {
  id: number;
  documentId: string;
  equipment?: string;
  condition?: string;
  objectType?: string;
  region?: string;
  amount?: string;
  deadline?: string;
  name?: string;
  phone?: string;
  comment?: string;
  inn?: string;
  advance?: string;
  form_source?: string;
  consent_at?: string;
  referrer?: string;
  type?: LeadType;
  template?: string;
  path?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_term?: string;
  yclid?: string;
  ym_uid?: string;
  ym_cookies?: string;
  lead_status?: LeadStatus | string;
  internal_comment?: string;
  cancel_reason?: LeadCancelReason | string | null;
  manager_edited_fields?: ManagerEditedFields | null;
  createdAt: string;
  updatedAt: string;
}

const LEAD_TYPES: LeadType[] = ['selection', 'insurance', 'leasing'];

const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  selection: 'Подбор',
  insurance: 'Страхование',
  leasing: 'Лизинг',
};

const getLeadTypeLabel = (type: LeadType | '') =>
  type ? LEAD_TYPE_LABELS[type] : 'Не указан';

const EDITABLE_TEXT_FIELDS: Array<{
  key: EditableLeadField;
  label: string;
  multiline?: boolean;
}> = [
  { key: 'name', label: 'Имя' },
  { key: 'phone', label: 'Телефон' },
  { key: 'comment', label: 'Комментарий', multiline: true },
];

const DETAIL_TEXT_FIELDS: Array<{
  key: EditableLeadField;
  label: string;
}> = [
  { key: 'equipment', label: 'Что нужно подобрать' },
  { key: 'condition', label: 'Состояние' },
  { key: 'objectType', label: 'Тип объекта' },
  { key: 'inn', label: 'ИНН' },
  { key: 'region', label: 'Регион' },
  { key: 'amount', label: 'Сумма / бюджет' },
  { key: 'advance', label: 'Аванс' },
  { key: 'deadline', label: 'Срок' },
];

const ALL_EDITABLE_FIELDS: EditableLeadField[] = [
  'name',
  'phone',
  'comment',
  'type',
  'equipment',
  'condition',
  'objectType',
  'inn',
  'region',
  'amount',
  'advance',
  'deadline',
];

const emptyDraftFromLead = (lead: Lead): Record<EditableLeadField, string> => ({
  name: lead.name || '',
  phone: lead.phone || '',
  comment: lead.comment || '',
  type: lead.type || '',
  equipment: lead.equipment || '',
  condition: lead.condition || '',
  objectType: lead.objectType || '',
  inn: lead.inn || '',
  region: lead.region || '',
  amount: lead.amount || '',
  advance: lead.advance || '',
  deadline: lead.deadline || '',
});

const normalizeComparable = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim();

const syncFormFromLead = (data: Lead) => ({
  fieldDrafts: emptyDraftFromLead(data),
  internalComment: data.internal_comment || '',
  leadStatusDraft: normalizeLeadStatus(data.lead_status),
  cancelReasonDraft: data.cancel_reason || '',
});

export const LeadsView = () => {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [internalComment, setInternalComment] = useState('');
  const [leadStatusDraft, setLeadStatusDraft] = useState<LeadStatus>('new');
  const [cancelReasonDraft, setCancelReasonDraft] = useState<LeadCancelReason | ''>('');
  const [fieldDrafts, setFieldDrafts] = useState<Record<EditableLeadField, string> | null>(null);
  const [phoneError, setPhoneError] = useState(false);
  const [showTypeChangeDialog, setShowTypeChangeDialog] = useState(false);
  const [pendingLeadType, setPendingLeadType] = useState<LeadType | null>(null);

  const getLead = async () => {
    if (!uid) return;

    try {
      setLoading(true);
      const response = await apiClient.get(`/leads/${uid}`);
      const data = response.data.data as Lead;
      const synced = syncFormFromLead(data);
      setLead(data);
      setFieldDrafts(synced.fieldDrafts);
      setInternalComment(synced.internalComment);
      setLeadStatusDraft(synced.leadStatusDraft);
      setCancelReasonDraft(synced.cancelReasonDraft as LeadCancelReason | '');
      setPhoneError(false);
    } catch (error) {
      console.error('Ошибка при загрузке заявки:', error);
      navigate(ROUTE_PATHS.ADMIN_LEADS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLead();
  }, [uid]);

  const applyLeadUpdate = (updated: Lead) => {
    const synced = syncFormFromLead(updated);
    setLead(updated);
    setFieldDrafts(synced.fieldDrafts);
    setInternalComment(synced.internalComment);
    setLeadStatusDraft(synced.leadStatusDraft);
    setCancelReasonDraft(synced.cancelReasonDraft as LeadCancelReason | '');
    setPhoneError(false);
  };

  const hasFormChanges = () => {
    if (!lead || !fieldDrafts) return false;

    for (const field of ALL_EDITABLE_FIELDS) {
      const draftValue =
        field === 'type' ? fieldDrafts.type : fieldDrafts[field].trim();
      const currentValue = normalizeComparable(lead[field]);
      if (draftValue !== currentValue) {
        return true;
      }
    }

    if (internalComment.trim() !== (lead.internal_comment || '').trim()) {
      return true;
    }

    if (
      normalizeLeadStatus(leadStatusDraft) !== normalizeLeadStatus(lead.lead_status) &&
      normalizeLeadStatus(leadStatusDraft) !== 'draft'
    ) {
      return true;
    }

    const nextCancel = cancelReasonDraft || null;
    const currentCancel = lead.cancel_reason || null;
    if (nextCancel !== currentCancel) {
      return true;
    }

    return false;
  };

  const getCurrentLeadType = (): LeadType | '' => {
    if (!fieldDrafts) return lead?.type || '';
    return (fieldDrafts.type as LeadType) || lead?.type || '';
  };

  const handleLeadTypeChangeRequest = (nextType: LeadType) => {
    const currentType = getCurrentLeadType();
    if (!nextType || nextType === currentType) return;

    setPendingLeadType(nextType);
    setShowTypeChangeDialog(true);
  };

  const confirmLeadTypeChange = () => {
    if (!fieldDrafts || !pendingLeadType) return;

    setFieldDrafts({
      ...fieldDrafts,
      type: pendingLeadType,
    });
    setShowTypeChangeDialog(false);
    setPendingLeadType(null);
  };

  const cancelLeadTypeChange = () => {
    setShowTypeChangeDialog(false);
    setPendingLeadType(null);
  };

  const handleSave = async () => {
    if (!lead || !fieldDrafts) return;

    if (!fieldDrafts.phone.trim()) {
      setPhoneError(true);
      return;
    }

    const payload: Partial<Lead> = {};

    for (const field of ALL_EDITABLE_FIELDS) {
      if (field === 'type') {
        const nextType = fieldDrafts.type;
        if (nextType && nextType !== normalizeComparable(lead.type)) {
          payload.type = nextType as LeadType;
        }
        continue;
      }

      const nextValue = fieldDrafts[field].trim();
      const currentValue = normalizeComparable(lead[field]);
      if (nextValue !== currentValue) {
        (payload as Record<string, string | null>)[field] = nextValue || null;
      }
    }

    const nextComment = internalComment.trim();
    const currentComment = (lead.internal_comment || '').trim();
    if (nextComment !== currentComment) {
      payload.internal_comment = nextComment || undefined;
    }

    const nextStatus = normalizeLeadStatus(leadStatusDraft);
    const currentStatus = normalizeLeadStatus(lead.lead_status);
    if (nextStatus !== currentStatus && nextStatus !== 'draft') {
      payload.lead_status = nextStatus;
    }

    const nextCancel = cancelReasonDraft || null;
    const currentCancel = lead.cancel_reason || null;
    if (nextCancel !== currentCancel) {
      payload.cancel_reason = nextCancel;
    }

    if (Object.keys(payload).length === 0) {
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.put(`/leads/${lead.documentId || lead.id}`, {
        data: payload,
      });
      const updated = (response.data?.data as Lead | undefined) || {
        ...lead,
        ...payload,
      };
      applyLeadUpdate(updated);
    } catch (error) {
      console.error('Ошибка при сохранении заявки:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isFieldEdited = (field: EditableLeadField) =>
    Boolean(lead?.manager_edited_fields?.[field]);

  const editedFieldTitle = (field: EditableLeadField) => {
    const entry = lead?.manager_edited_fields?.[field];
    if (!entry) return undefined;
    const original =
      entry.original_value === null || entry.original_value === ''
        ? 'пусто'
        : entry.original_value;
    return `Изменено менеджером ${formatDate(entry.date)}. Изначально: ${original}`;
  };

  const renderEditableTextField = (
    field: EditableLeadField,
    label: string,
    multiline = false,
  ) => {
    if (!fieldDrafts) return null;

    const edited = isFieldEdited(field);

    return (
      <ListItem
        key={field}
        className={clsx(styles.listItem, styles.listItemColumn, edited && styles.editedListItem)}
      >
        <b>{label}:</b>
        <Tooltip title={editedFieldTitle(field) || ''} disableHoverListener={!edited}>
          <TextField
            className={clsx(styles.editableField, edited && styles.editedField)}
            size='small'
            fullWidth
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            value={fieldDrafts[field]}
            onChange={(e) => {
              if (field === 'phone') {
                setPhoneError(false);
              }
              setFieldDrafts({
                ...fieldDrafts,
                [field]: e.target.value,
              });
            }}
            disabled={saving}
            error={field === 'phone' && phoneError}
            helperText={
              field === 'phone' && phoneError
                ? 'Телефон обязателен'
                : edited
                  ? 'Изменено менеджером'
                  : undefined
            }
          />
        </Tooltip>
      </ListItem>
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!lead || !fieldDrafts) {
    return (
      <Box className={styles.container}>
        <DashboardHeaderBackLink link={ROUTE_PATHS.ADMIN_LEADS} title={'Заявка не найдена'} />
        <Box style={{ textAlign: 'center', marginTop: '50px' }}>
          <p>Заявка не найдена или была удалена</p>
        </Box>
      </Box>
    );
  }

  const typeEdited = isFieldEdited('type');
  const currentLeadType = getCurrentLeadType();

  return (
    <Box className={styles.container}>
      <DashboardHeaderBackLink
        link={ROUTE_PATHS.ADMIN_LEADS}
        title={`Заявка с лендинга #${lead.id || lead.documentId}`}
      />

      <Box>
        <Card className={styles.card}>
          <CardHeader
            title={`Заявка с лендинга #${lead.id || lead.documentId}`}
            action={
              <Box className={styles.headerActions}>
                <FormControl size='small' className={styles.typeSelect}>
                  <Select
                    value={currentLeadType || ''}
                    onChange={(e) => handleLeadTypeChangeRequest(e.target.value as LeadType)}
                    disabled={saving}
                    className={clsx(
                      styles.statusSelectField,
                      styles.editableSelect,
                      typeEdited && styles.editedSelect,
                    )}
                    displayEmpty
                    renderValue={(value) => getLeadTypeLabel(value as LeadType | '')}
                  >
                    {!currentLeadType && (
                      <MenuItem value='' disabled>
                        Не указан
                      </MenuItem>
                    )}
                    {LEAD_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {LEAD_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ButtonCustom
                  className={clsx(styles.greenBtn, styles.saveBtn)}
                  onClick={handleSave}
                  disabled={saving || !hasFormChanges()}
                >
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </ButtonCustom>
              </Box>
            }
          />
          <CardContent className={styles.cardContent}>
            <List>
              <ListItem className={styles.sectionHeader}>
                <b>Контактная информация</b>
              </ListItem>

              {EDITABLE_TEXT_FIELDS.map(({ key, label, multiline }) =>
                renderEditableTextField(key, label, multiline),
              )}

              <ListItem className={styles.sectionHeader}>
                <b>Статус</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Статус:</b>
                <FormControl size='small' className={styles.statusSelect}>
                  <Select
                    value={leadStatusDraft}
                    onChange={(e) => setLeadStatusDraft(e.target.value as LeadStatus)}
                    disabled={saving}
                    className={clsx(
                      styles.statusSelectField,
                      styles[getLeadStatusClassName(leadStatusDraft)],
                    )}
                    renderValue={(value) => getLeadStatusLabel(value)}
                  >
                    {normalizeLeadStatus(lead.lead_status) === 'draft' && (
                      <MenuItem value='draft' disabled>
                        {getLeadStatusLabel('draft')}
                      </MenuItem>
                    )}
                    {EDITABLE_LEAD_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {getLeadStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Причина отмены:</b>
                <FormControl size='small' className={styles.statusSelect}>
                  <Select
                    displayEmpty
                    value={cancelReasonDraft}
                    onChange={(e) => setCancelReasonDraft(e.target.value as LeadCancelReason | '')}
                    disabled={saving}
                    className={styles.statusSelectField}
                    renderValue={(value) =>
                      value ? getLeadCancelReasonLabel(String(value)) : 'Не указана'
                    }
                  >
                    <MenuItem value=''>
                      <em>Не указана</em>
                    </MenuItem>
                    {LEAD_CANCEL_REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {LEAD_CANCEL_REASON_LABELS[reason]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>

              <ListItem className={clsx(styles.listItem, styles.listItemColumn)}>
                <b>Внутренний комментарий:</b>
                <TextField
                  className={styles.internalCommentField}
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder='Комментарий только для админки'
                  value={internalComment}
                  onChange={(e) => setInternalComment(e.target.value)}
                  disabled={saving}
                />
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Детали заявки</b>
              </ListItem>

              {DETAIL_TEXT_FIELDS.map(({ key, label }) => renderEditableTextField(key, label))}

              <ListItem className={styles.listItem}>
                <b>Согласие:</b>
                <span>{lead.consent_at ? formatDate(lead.consent_at) : '-'}</span>
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Источник</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Источник формы:</b>
                <span>{getLeadFormSourceLabel(lead.form_source)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Referrer:</b>
                <span>{lead.referrer || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Шаблон:</b>
                <span>{getLeadTemplateLabel(lead.template)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Страница:</b>
                <span>{getLeadPathLabel(lead.path)}</span>
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Кампания</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>utm_source:</b>
                <span>{lead.utm_source || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>utm_campaign:</b>
                <span>{lead.utm_campaign || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>utm_term:</b>
                <span>{lead.utm_term || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>yclid:</b>
                <span>{lead.yclid || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>ym_uid:</b>
                <span>{lead.ym_uid || '-'}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>ym_cookies:</b>
                <span>{lead.ym_cookies || '-'}</span>
              </ListItem>

              <ListItem className={styles.sectionHeader}>
                <b>Системная информация</b>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Создана:</b>
                <span>{formatDate(lead.createdAt)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Последнее изменение:</b>
                <span>{formatDate(lead.updatedAt)}</span>
              </ListItem>

              <ListItem className={styles.listItem}>
                <b>Document ID:</b>
                <span>{lead.documentId || '-'}</span>
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={showTypeChangeDialog}
        onClose={cancelLeadTypeChange}

      >
        <DialogTitle className={styles.modalTitle}>Изменение типа заявки</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы меняете тип заявки с «{getLeadTypeLabel(currentLeadType)}» на «
            {pendingLeadType ? LEAD_TYPE_LABELS[pendingLeadType] : ''}». Все уже заполненные поля
            останутся без изменений. Продолжить?
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <Button onClick={cancelLeadTypeChange} className={styles.modalButtonCancel}>
            Отмена
          </Button>
          <Button onClick={confirmLeadTypeChange} className={styles.greenBtn}>
            Изменить тип
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
